import { useCallback, useState } from 'react';
import ChatService from '../services/ChatService';
import SchedulesService from '../services/SchedulesService';
import { errorText } from '../utils/format';
import useAsync from './useAsync';

/** Recurring messages: list, create, edit, switch on or off, run now, delete. */
export function useSchedulesViewModel() {
  const load = useCallback(async () => {
    const [schedules, chats] = await Promise.all([SchedulesService.list(), ChatService.conversations()]);
    return { schedules, chats };
  }, []);
  const { data, loading, error, reload } = useAsync(load);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [feedback, setFeedback] = useState(null);

  const run = async (fn, successText) => {
    setSaving(true);
    setSaveError('');
    try {
      await fn();
      await reload({ silent: true });
      if (successText) setFeedback({ severity: 'success', text: successText });
      return true;
    } catch (err) {
      setSaveError(errorText(err));
      if (!successText) return false;
      setFeedback({ severity: 'error', text: errorText(err) });
      return false;
    } finally {
      setSaving(false);
    }
  };

  return {
    schedules: data?.schedules || [],
    chats: data?.chats || [],
    loading,
    error,
    reload,
    saving,
    saveError,
    clearSaveError: () => setSaveError(''),
    feedback,
    clearFeedback: () => setFeedback(null),
    create: (s) => run(() => SchedulesService.create(s)),
    update: (id, fields) => run(() => SchedulesService.update(id, fields)),
    toggle: (s) => run(() => SchedulesService.update(s.id, { enabled: !s.enabled }), s.enabled ? 'Schedule switched off.' : 'Schedule switched on.'),
    remove: (id) => run(() => SchedulesService.remove(id), 'Schedule deleted.'),
    runNow: (id) => run(() => SchedulesService.runNow(id), 'Started. Open the chat to follow along.'),
  };
}
